import { FC, useEffect } from "react";

import Button from "components/Button";
import InfoCard from "components/InfoCard";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { removeCompanyRequest, resetSuccessfullyRemovedCompany } from "store/slices/companiesSlice";
import { showToast } from "store/slices/toastSlice";

import { useNavigate } from "react-router-dom";
import { getApiErrorMessage } from "utils/helpers";
import { PAGES } from "utils/links";
import styles from "../styles.module.scss";

type PropTypes = {
  onClose?: () => void;
  name?: string;
  portfolios?: string;
  assets?: string;
  users?: string;
  id?: number;
};

const ModalRemoveCompanyContent: FC<PropTypes> = ({ onClose, name, portfolios, assets, users, id }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, successfullyRemovedId, errors } = useAppSelector((state: RootStore) => state.companies);

  const handleDelete = () => {
    dispatch(removeCompanyRequest({ id: Number(id) }));
  };

  const handleCancel = () => {
    onClose?.();
  };

  useEffect(() => {
    if (successfullyRemovedId) {
      onClose?.();
      dispatch(
        showToast({
          id: "successfully-removed-company",
          message: "The company was successfully deleted.",
          type: "success",
          duration: 3000,
        }),
      );
      dispatch(resetSuccessfullyRemovedCompany());
      navigate(PAGES.COMPANY_LIST);
    }
  }, [successfullyRemovedId, onClose, dispatch, navigate]);

  useEffect(() => {
    if (errors) {
      dispatch(
        showToast({
          id: "error-removing-company",
          message: getApiErrorMessage(errors, "Failed to delete company. Please try again later."),
          type: "error",
          duration: 3000,
        }),
      );
    }
  }, [errors, dispatch]);

  return (
    <div className={styles.content}>
      <p className={styles.contentTitle}>
        Are you sure you want to delete <span>"{name}"</span>?
      </p>

      <InfoCard color="red" variant="red" size="small">
        <p className={styles.contentInfoTitle}>Warning: This company contains data</p>

        <ul className={styles.contentInfoList}>
          <li>
            {portfolios} portfolio{portfolios !== "1" ? "s" : ""} will be deleted
          </li>
          <li>
            {assets} asset{assets !== "1" ? "s" : ""} will be deleted
          </li>
          <li>
            {users} user{users !== "1" ? "s" : ""} will lose access
          </li>
          <li>All sensors and gateways will be removed from the LoRaWAN network</li>
          <li>All historical sensor metrics, events, and uplink history will be permanently lost</li>
        </ul>
      </InfoCard>

      <p>This action cannot be undone. All associated data will be permanently deleted.</p>

      <div className={styles.buttons}>
        <Button variant="secondary" onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>

        <Button variant="primary" onClick={handleDelete} disabled={isLoading}>
          Delete
        </Button>
      </div>
    </div>
  );
};

export default ModalRemoveCompanyContent;
